import Flex from '@Resources/styles/flex.scss';
import TableStyles from '@Resources/styles/tables.scss';
import DialogsStyles from '@Styles/dialogs.scss';
import { SortOrder } from '@Utils/helpers/sortingHelpers';
import { getHumanReadableDateTime } from '@Utils/helpers/stringHelpers';
import DescendingIcon from '@mui/icons-material/ArrowDropDown';
import AscendingIcon from '@mui/icons-material/ArrowDropUp';
import { CircularProgress, Dialog } from '@mui/material';
import cx from 'classnames';
import _isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalBody from '@Components/ModalComponents/ModalBody';
import ModalFooter from '@Components/ModalComponents/ModalFooter';
import ModalHeader from '@Components/ModalComponents/ModalHeader';
import FilesApi from '@Data/files/Api';
import { DownloadFilesSortField, IQcFileResult } from '@Data/files/Interfaces';
import {
    downloadFilesSortFieldState,
    downloadFilesSortOrderState,
    sortedDownloadFilesState,
} from '@Data/files/Reducer';
import Styles from './DownloadReports.scss';

const DownloadReportsDialogue = (props) => {
    const { isOpen, selectedFolder, onCancel, onComplete } = props;

    const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<IQcFileResult>(null);
    const files = useSelector(sortedDownloadFilesState);
    const sortField = useSelector(downloadFilesSortFieldState);
    const sortOrder = useSelector(downloadFilesSortOrderState);

    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    useEffect(() => {
        if (!_isNil(selectedFolder)) {
            setIsLoadingFiles(true);
            FilesApi.getQcResults(selectedFolder.folder_id, null, () => {
                setIsLoadingFiles(false);
            });
        }
    }, [selectedFolder]);

    const handleSubmit = () => {
        setIsDownloading(true);

        FilesApi.downloadFileFromUrl(
            selectedFile.report_url,
            `${selectedFile.QC_inspection_name}.pdf`,
            () => {
                setIsDownloading(false);
                onComplete();
            }
        );
    };

    const fileOptions = files.map((f: IQcFileResult, i) => {
        return (
            <tr
                id={`file-option-${i}}`}
                key={`fileOpt${f.url}`}
                onClick={() => setSelectedFile(f)}
                className={
                    f.url === selectedFile?.url ? TableStyles.selectedRow : ''
                }
            >
                <td>{f.QC_inspection_name}</td>
                <td>{getHumanReadableDateTime(f.QC_inspection_timestamp)}</td>
                <td>{f.QC_inspection_user_name}</td>
            </tr>
        );
    });

    const handleTableSorting = (field: DownloadFilesSortField) => {
        let order = SortOrder.ASC;
        if (field === sortField) {
            order =
                sortOrder === SortOrder.DESC ? SortOrder.ASC : SortOrder.DESC;
        }

        FilesApi.setTableSorting(field, order);
    };

    const sortOrderIcon =
        sortOrder === SortOrder.DESC ? <DescendingIcon /> : <AscendingIcon />;

    const innerHtml = isLoadingFiles ? (
        <div className={cx(Flex.flex, Flex.justifyCenter)}>
            <CircularProgress />
        </div>
    ) : files.length === 0 ? (
        <div
            id="reports-dialog-no-files-text"
            className={cx(Flex.flex, Flex.justifyCenter)}
        >
            No QC inspections found
        </div>
    ) : (
        <table
            id="reports-dialog-files-table"
            className={Styles.fileDownloadTable}
        >
            <thead>
                <tr>
                    <td
                        onClick={() =>
                            handleTableSorting(DownloadFilesSortField.TITLE)
                        }
                        className={
                            sortField === DownloadFilesSortField.TITLE
                                ? TableStyles.selectedHeader
                                : null
                        }
                        style={{ width: '60%' }}
                    >
                        <div>
                            File name
                            {sortField === DownloadFilesSortField.TITLE && (
                                <React.Fragment>{sortOrderIcon}</React.Fragment>
                            )}
                        </div>
                    </td>
                    <td
                        onClick={() =>
                            handleTableSorting(
                                DownloadFilesSortField.CREATED_DATE
                            )
                        }
                        className={
                            sortField === DownloadFilesSortField.CREATED_DATE
                                ? TableStyles.selectedHeader
                                : null
                        }
                        style={{ width: '20%' }}
                    >
                        <div>
                            Date created
                            {sortField ===
                                DownloadFilesSortField.CREATED_DATE && (
                                <React.Fragment>{sortOrderIcon}</React.Fragment>
                            )}
                        </div>
                    </td>
                    <td
                        onClick={() =>
                            handleTableSorting(DownloadFilesSortField.AUTHOR)
                        }
                        className={
                            sortField === DownloadFilesSortField.AUTHOR
                                ? TableStyles.selectedHeader
                                : null
                        }
                        style={{ width: '20%' }}
                    >
                        <div>
                            Author
                            {sortField === DownloadFilesSortField.AUTHOR && (
                                <React.Fragment>{sortOrderIcon}</React.Fragment>
                            )}
                        </div>
                    </td>
                </tr>
            </thead>
            <tbody>{fileOptions}</tbody>
        </table>
    );

    return (
        <Dialog
            id="downlaod-reports-dialog"
            open={isOpen}
            maxWidth="lg"
            sx={{
                ['& .MuiDialog-container']: {
                    alignItems: 'flex-start',
                    marginTop: '5rem',
                },
            }}
        >
            <ModalHeader
                id="downlaod-reports-dialog-header"
                title={`Files - ${selectedFolder?.folder_name}`}
                onCancel={onCancel}
                isCancelDisabled={isDownloading}
            />
            <ModalBody>
                <div className={DialogsStyles.processModalComponentContainer}>
                    <div className={cx(Flex.flex, Flex.column)}>
                        {innerHtml}
                    </div>
                </div>
            </ModalBody>
            <ModalFooter
                onCancel={onCancel}
                onSubmit={handleSubmit}
                submitText="Download"
                canSubmit={!_isNil(selectedFile)}
                isSubmitting={isDownloading}
            />
        </Dialog>
    );
};

DownloadReportsDialogue.propTypes = {
    isOpen: PropTypes.bool,
    onCancel: PropTypes.func,
    onComplete: PropTypes.func,
    selectedFolder: PropTypes.any,
};
export default DownloadReportsDialogue;
